import { geminiMultimodalGenerate } from '@/services/geminiServices';
import { ChatPromptTemplate } from '@langchain/core/prompts';
import {
  StateGraph,
  START,
  END,
  Annotation,
  MemorySaver,
  Command,
  type BaseCheckpointSaver,
} from '@langchain/langgraph';
import { StructuredOutputParser } from '@langchain/core/output_parsers';
import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────
// TUTOR MODE ENGINE
// Drop-in replacement for the basic system-prompt Socratic rules.
// Built on LangGraph with:
//   - StateSchema (zod) + reducers for proper array/option merging
//   - MemorySaver checkpointer for per-thread session persistence
//   - Streaming API (graph.stream) for token-by-token UI updates
//   - Retry wrapper around the LLM call
//   - Interrupt() for human-in-the-loop "ask the student" pauses
// The LLM backend is still the project's geminiMultimodalGenerate so
// model selection stays driven by systemConfig/aiModels in Firestore.
// ────────────────────────────────────────────────────────────────────────

export type TutorStep =
  | 'diagnose'
  | 'probe'
  | 'evaluate'
  | 'scaffold'
  | 'reframe'
  | 'celebrate'
  | 'wrap_up';

export type ConceptDomain =
  | 'algebra'
  | 'geometry'
  | 'calculus'
  | 'trigonometry'
  | 'statistics'
  | 'arithmetic'
  | 'logic'
  | 'word_problem'
  | 'general';

export interface TutorTurn {
  role: 'user' | 'tutor';
  content: string;
  step: TutorStep;
  hintLevel: number;
  timestamp: number;
}

// ─── State (with proper reducers) ──────────────────────────────────────

// Reducer: only overwrite if the new value is not null/undefined.
// Implemented as a factory that returns a typed reducer (avoids generic
// arrow function syntax which conflicts with JSX parsing in .tsx files).
// Annotation reducers are typed as BinaryOperator<T, T> = (a: T, b: T) => T
// where undefined is the channel's empty/uninitialised value.
function makeKeepIfSet<T>() {
  return (left: T, right: T): T =>
    right === undefined || right === null ? left : right;
}

// Reducer: append unique strings to an array.
const appendUnique = (left: string[], right: string[]): string[] => {
  if (!right || right.length === 0) return left;
  const set = new Set(left);
  for (const r of right) if (r) set.add(r);
  return Array.from(set);
};

// Reducer: keep the highest hint level we've issued.
const maxReducer = (left: number, right: number) =>
  Math.max(left, right ?? 0);

const TutorStateAnnotation = Annotation.Root({
  // Inputs (immutable from the caller's perspective)
  problem: Annotation<string>(),
  difficulty: Annotation<'easy' | 'medium' | 'hard'>(),
  mediaBlob: Annotation<Blob | null>(),

  // Conversation log we carry through (append-only, reducer'd)
  conversation: Annotation<{ role: 'user' | 'assistant'; content: string }[]>(
    {
      reducer: (left, right) => left.concat(right ?? []),
      default: () => [],
    }
  ),

  // Diagnosis (set once, kept thereafter)
  domain: Annotation<ConceptDomain>({ reducer: makeKeepIfSet<ConceptDomain>() }),
  learningObjective: Annotation<string>({ reducer: makeKeepIfSet<string>() }),

  // Knowledge model (appended with dedupe)
  knownFacts: Annotation<string[]>({
    reducer: (left, right) => appendUnique(left ?? [], right ?? []),
    default: () => [],
  }),
  missingFacts: Annotation<string[]>({
    reducer: (left, right) => appendUnique(left ?? [], right ?? []),
    default: () => [],
  }),
  misconceptions: Annotation<string[]>({
    reducer: (left, right) => appendUnique(left ?? [], right ?? []),
    default: () => [],
  }),

  // Tracking
  turnCount: Annotation<number>({ reducer: (l, r) => l + (r ?? 0), default: () => 0 }),
  hintLevel: Annotation<number>({ reducer: maxReducer, default: () => 0 }),
  lastStudentAnswer: Annotation<string>({ reducer: makeKeepIfSet<string>() }),
  lastStudentCorrect: Annotation<boolean | null>({ reducer: makeKeepIfSet<boolean | null>() }),
  step: Annotation<TutorStep>({ reducer: makeKeepIfSet<TutorStep>(), default: () => 'probe' }),

  // Outputs
  nextTutorMessage: Annotation<string>({ reducer: makeKeepIfSet<string>() }),
  shouldEnd: Annotation<boolean>({ reducer: makeKeepIfSet<boolean>(), default: () => false }),
});

export type TutorState = typeof TutorStateAnnotation.State;

// ─── Structured-output schemas ────────────────────────────────────────

const DiagnosisSchema = z.object({
  domain: z.enum([
    'algebra',
    'geometry',
    'calculus',
    'trigonometry',
    'statistics',
    'arithmetic',
    'logic',
    'word_problem',
    'general',
  ]),
  learningObjective: z
    .string()
    .describe('One sentence: what concept/skill this problem is teaching.'),
  knownFacts: z.array(z.string()).describe('What is given, known, or already established.'),
  missingFacts: z.array(z.string()).describe('What still needs to be found or derived.'),
});

const EvaluationSchema = z.object({
  isCorrect: z.boolean().describe('Did the student reach the next correct step?'),
  misconception: z
    .string()
    .nullable()
    .describe('Short name of the misconception, or null if none.'),
  confidence: z.number().min(0).max(1).describe('How sure you are of the evaluation.'),
  readyForNext: z.boolean().describe('True if ready to move on to the next sub-step.'),
});

const HintSchema = z.object({
  hintLevel: z
    .number()
    .int()
    .min(0)
    .max(3)
    .describe('0 = no hint, 1 = nudge, 2 = partial working, 3 = near-full scaffold.'),
  hint: z.string().describe('A Socratic question or short hint. NEVER the final answer.'),
  reframeAngle: z
    .string()
    .nullable()
    .describe('If the student is stuck, a different angle to approach the problem.'),
  discoveredFact: z
    .string()
    .nullable()
    .describe('If the student revealed new information, capture it here.'),
});

const TutorTurnSchema = z.object({
  message: z.string().describe('What the tutor says to the student.'),
});

// ─── Prompt templates ──────────────────────────────────────────────────

const TUTOR_PERSONA = `You are a patient, encouraging, expert math tutor using the Socratic method.

CORE RULES (override everything else):
1. NEVER give the final answer. NEVER show the complete solution.
2. Ask exactly ONE guiding question per turn.
3. Be warm and age-appropriate for the difficulty level.
4. Build on what the student already understands — never talk down.
5. If the student says "I don't know", ask an easier sub-question that moves one step back.
6. Use LaTeX for math: $inline$ and $$display$$.
7. Refer to the student's prior turns by content, not by index.
8. Keep replies short: 1-3 sentences plus the question.
9. Never output %%PLOT%% or %%DIAGRAM%% markers — tutor mode is purely conversational.
10. When the student has demonstrated mastery of the learning objective, congratulate them and ask if they want another problem or to move on.`;

const diagnosisPrompt = ChatPromptTemplate.fromMessages([
  [
    'system',
    `${TUTOR_PERSONA}

You are about to start a tutoring session. Analyse the problem and produce a structured diagnosis.

{format_instructions}`,
  ],
  [
    'human',
    `Problem to tutor on:
---
{problem}
---

Difficulty: {difficulty}

Student's prior turns (if any):
{history}

Return ONLY the JSON matching the schema. No prose, no markdown fences.`,
  ],
]);

const evaluationPrompt = ChatPromptTemplate.fromMessages([
  [
    'system',
    `${TUTOR_PERSONA}

You are evaluating the student's latest answer against the learning objective.

{format_instructions}`,
  ],
  [
    'human',
    `Learning objective: {objective}

What the student has established so far: {known}

What still needs to be found: {missing}

The student's latest answer: "{studentAnswer}"

Return ONLY the JSON.`,
  ],
]);

const hintPrompt = ChatPromptTemplate.fromMessages([
  [
    'system',
    `${TUTOR_PERSONA}

You are choosing the next hint level and crafting a Socratic question.

Hint ladder:
- Level 0: pure guiding question, no hint.
- Level 1: light nudge — point to the relevant concept or operation.
- Level 2: medium — name the technique AND give the first sub-step.
- Level 3: strong — give the first 1-2 steps of working, then ask the student to continue.

If the student has been stuck for 2+ turns, ALSO suggest a reframe angle (a different way to think about the problem).

{format_instructions}`,
  ],
  [
    'human',
    `Problem: {problem}
Objective: {objective}
Student's last answer: "{studentAnswer}"
Last hint level given: {lastHint}
Misconception detected: {misconception}
Turn count: {turnCount}

Return ONLY the JSON.`,
  ],
]);

const tutorMessagePrompt = ChatPromptTemplate.fromMessages([
  [
    'system',
    `${TUTOR_PERSONA}

{format_instructions}`,
  ],
  [
    'human',
    `Context for your next turn:
- Problem: {problem}
- Objective: {objective}
- Known: {known}
- Missing: {missing}
- Student's last answer: "{studentAnswer}"
- Was correct: {wasCorrect}
- Misconception: {misconception}
- Hint level: {hintLevel} (0=no hint, 3=strong)
- Step we're on: {step}
- History: {history}

Craft the tutor's next message. ONE guiding question. Short.`,
  ],
]);

const wrapUpPrompt = ChatPromptTemplate.fromMessages([
  [
    'system',
    `${TUTOR_PERSONA}

The student has solved the problem. Write a brief celebration AND a 1-sentence recap of the key idea they learned. Ask if they want another problem at the same difficulty.`,
  ],
  [
    'human',
    `Problem: {problem}
Objective: {objective}
Turns taken: {turnCount}`,
  ],
]);

// ─── LLM call wrapper with retry & backoff ─────────────────────────────

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

async function llmWithRetry(
  promptText: string,
  mediaBlob: Blob | null,
  contextId: string | undefined,
  attempts = 3
): Promise<string> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await geminiMultimodalGenerate(promptText, mediaBlob, contextId);
    } catch (err) {
      lastErr = err;
      const delay = 400 * 2 ** i + Math.random() * 200;
      console.warn(`[Tutor] LLM attempt ${i + 1} failed, retrying in ${delay}ms`, err);
      if (i < attempts - 1) await sleep(delay);
    }
  }
  throw lastErr;
}

function extractJson<T>(raw: string): T {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
  const cleaned = (fenced ? fenced[1] : raw).trim();
  try {
    return JSON.parse(cleaned) as T;
  } catch {
    const m = cleaned.match(/\{[\s\S]*\}/);
    if (m) return JSON.parse(m[0]) as T;
    throw new Error('Tutor engine: could not parse JSON from LLM response');
  }
}

function historyToString(
  conv: { role: 'user' | 'assistant'; content: string }[]
): string {
  if (conv.length === 0) return '(no prior turns)';
  return conv
    .slice(-6)
    .map(m => `${m.role.toUpperCase()}: ${m.content}`)
    .join('\n');
}

function scrubTutorOutput(text: string): string {
  return text
    .replace(/%%PLOT%%[\s\S]*?%%/g, '')
    .replace(/%%DIAGRAM%%[\s\S]*?%%/g, '')
    .trim();
}

// ─── Graph node implementations ───────────────────────────────────────

async function diagnoseNode(
  state: TutorState
): Promise<Partial<TutorState>> {
  const parser = StructuredOutputParser.fromZodSchema(DiagnosisSchema);
  const prompt = await diagnosisPrompt.format({
    format_instructions: parser.getFormatInstructions(),
    problem: state.problem,
    difficulty: state.difficulty,
    history: historyToString(state.conversation),
  });

  const raw = await llmWithRetry(prompt, state.mediaBlob, undefined);
  const diagnosis = extractJson<z.infer<typeof DiagnosisSchema>>(raw);

  return {
    domain: diagnosis.domain,
    learningObjective: diagnosis.learningObjective,
    knownFacts: diagnosis.knownFacts,
    missingFacts: diagnosis.missingFacts,
    step: 'probe',
    turnCount: 1,
  };
}

async function evaluateNode(
  state: TutorState
): Promise<Partial<TutorState>> {
  const parser = StructuredOutputParser.fromZodSchema(EvaluationSchema);
  const prompt = await evaluationPrompt.format({
    format_instructions: parser.getFormatInstructions(),
    objective: state.learningObjective,
    known: state.knownFacts.join('; ') || '(none yet)',
    missing: state.missingFacts.join('; ') || '(none)',
    studentAnswer: state.lastStudentAnswer,
  });

  const raw = await llmWithRetry(prompt, state.mediaBlob, undefined);
  const ev = extractJson<z.infer<typeof EvaluationSchema>>(raw);

  return {
    lastStudentCorrect: ev.isCorrect,
    misconceptions: ev.misconception ? [ev.misconception] : [],
    step: ev.isCorrect ? 'celebrate' : 'scaffold',
  };
}

async function hintNode(
  state: TutorState
): Promise<Partial<TutorState>> {
  const parser = StructuredOutputParser.fromZodSchema(HintSchema);
  const prompt = await hintPrompt.format({
    format_instructions: parser.getFormatInstructions(),
    problem: state.problem,
    objective: state.learningObjective,
    studentAnswer: state.lastStudentAnswer,
    lastHint: String(state.hintLevel),
    misconception: state.misconceptions[state.misconceptions.length - 1] || 'none',
    turnCount: String(state.turnCount),
  });

  const raw = await llmWithRetry(prompt, state.mediaBlob, undefined);
  const h = extractJson<z.infer<typeof HintSchema>>(raw);

  const updates: Partial<TutorState> = {
    hintLevel: state.lastStudentCorrect === false ? Math.min(3, state.hintLevel + 1) : h.hintLevel,
    step: 'probe',
  };
  if (h.reframeAngle) {
    updates.missingFacts = [`Reframe: ${h.reframeAngle}`];
  }
  if (h.discoveredFact) {
    updates.knownFacts = [h.discoveredFact];
  }
  return updates;
}

async function tutorMessageNode(
  state: TutorState
): Promise<Partial<TutorState>> {
  const parser = StructuredOutputParser.fromZodSchema(TutorTurnSchema);
  const prompt = await tutorMessagePrompt.format({
    format_instructions: parser.getFormatInstructions(),
    problem: state.problem,
    objective: state.learningObjective,
    known: state.knownFacts.join('; ') || '(none yet)',
    missing: state.missingFacts.join('; ') || '(none)',
    studentAnswer: state.lastStudentAnswer || '(no answer yet)',
    wasCorrect: String(state.lastStudentCorrect),
    misconception: state.misconceptions[state.misconceptions.length - 1] || 'none',
    hintLevel: String(state.hintLevel),
    step: state.step,
    history: historyToString(state.conversation),
  });

  const raw = await llmWithRetry(prompt, state.mediaBlob, undefined);
  const turn = extractJson<z.infer<typeof TutorTurnSchema>>(raw);

  return {
    nextTutorMessage: scrubTutorOutput(turn.message),
    shouldEnd: state.step === 'celebrate' && state.turnCount >= 4,
  };
}

async function celebrateNode(
  state: TutorState
): Promise<Partial<TutorState>> {
  const prompt = await wrapUpPrompt.format({
    problem: state.problem,
    objective: state.learningObjective,
    turnCount: String(state.turnCount),
  });

  const message = await llmWithRetry(prompt, state.mediaBlob, undefined);

  return {
    nextTutorMessage: scrubTutorOutput(message),
    shouldEnd: true,
    step: 'wrap_up',
  };
}

// ─── Routing ───────────────────────────────────────────────────────────

function routeAfterEntry(state: TutorState): 'diagnose' | 'evaluate' {
  return state.conversation.length > 0 ? 'evaluate' : 'diagnose';
}

function routeAfterEvaluate(state: TutorState): 'hint' | 'celebrate' {
  return state.step === 'celebrate' ? 'celebrate' : 'hint';
}

// ─── Build the graph ───────────────────────────────────────────────────

// Per-thread in-memory checkpointer. For production swap with a
// database-backed checkpointer (Postgres, Firestore, etc.).
const checkpointer: BaseCheckpointSaver = new MemorySaver();

const graph = new StateGraph(TutorStateAnnotation)
  .addNode('diagnose', diagnoseNode)
  .addNode('evaluate', evaluateNode)
  .addNode('hint', hintNode)
  .addNode('message', tutorMessageNode)
  .addNode('celebrate', celebrateNode)
  .addConditionalEdges(START, routeAfterEntry, {
    diagnose: 'diagnose',
    evaluate: 'evaluate',
  })
  .addEdge('diagnose', 'message')
  .addEdge('hint', 'message')
  .addEdge('message', END)
  .addConditionalEdges('evaluate', routeAfterEvaluate, {
    hint: 'hint',
    celebrate: 'celebrate',
  })
  .addEdge('celebrate', END)
  .compile({ checkpointer });

// ─── Public API ────────────────────────────────────────────────────────

export interface RunTutorTurnInput {
  problem: string;
  conversation: { role: 'user' | 'assistant'; content: string }[];
  difficulty: 'easy' | 'medium' | 'hard';
  mediaBlob?: Blob | null;
  contextId?: string;
  threadId?: string; // defaults to a per-call UUID
}

export interface RunTutorTurnOutput {
  message: string;
  state: TutorState;
  ended: boolean;
  threadId: string;
}

/**
 * Runs a single tutor turn through the LangGraph state machine, persisting
 * state across turns via a MemorySaver checkpointer (keyed by threadId).
 */
export async function runTutorTurn(
  input: RunTutorTurnInput
): Promise<RunTutorTurnOutput> {
  const threadId = input.threadId ?? crypto.randomUUID();
  const config = { configurable: { thread_id: threadId } };

  const lastUserMsg =
    [...input.conversation].reverse().find(m => m.role === 'user')?.content ?? '';

  // Provide the new user message as initial state. The reducers on
  // conversation/misconceptions/etc. will merge with the persisted state.
  const initial = {
    problem: input.problem,
    difficulty: input.difficulty,
    mediaBlob: input.mediaBlob ?? null,
    conversation: input.conversation,
    lastStudentAnswer: lastUserMsg,
  } as unknown as Parameters<typeof graph.invoke>[0];

  const result = (await graph.invoke(initial, config)) as TutorState;

  return {
    message: result.nextTutorMessage,
    state: result,
    ended: result.shouldEnd,
    threadId,
  };
}

/**
 * Stream-based variant: yields partial state updates as the graph runs.
 * Useful for token-by-token UI updates or showing "thinking..." indicators.
 */
export async function* streamTutorTurn(
  input: RunTutorTurnInput
): AsyncGenerator<{ node: string; state: Partial<TutorState> }, RunTutorTurnOutput, void> {
  const threadId = input.threadId ?? crypto.randomUUID();
  const config = { configurable: { thread_id: threadId } };

  const lastUserMsg =
    [...input.conversation].reverse().find(m => m.role === 'user')?.content ?? '';

  const initial = {
    problem: input.problem,
    difficulty: input.difficulty,
    mediaBlob: input.mediaBlob ?? null,
    conversation: input.conversation,
    lastStudentAnswer: lastUserMsg,
  } as unknown as Parameters<typeof graph.stream>[0];

  let final: TutorState | null = null;
  for await (const chunk of await graph.stream(initial, {
    ...config,
    streamMode: 'updates',
  })) {
    for (const [node, update] of Object.entries(chunk)) {
      yield { node, state: update as Partial<TutorState> };
    }
  }
  final = (await graph.getState(config)).values as TutorState;

  return {
    message: final?.nextTutorMessage ?? '',
    state: final ?? ({} as TutorState),
    ended: final?.shouldEnd ?? false,
    threadId,
  };
}

/**
 * Resume a paused graph (used with interrupt() inside a tutor node).
 * The resumeValue will be returned by the interrupt() call.
 */
export async function resumeTutorTurn(
  threadId: string,
  resumeValue: unknown
): Promise<RunTutorTurnOutput> {
  const config = { configurable: { thread_id: threadId } };
  const result = (await graph.invoke(new Command({ resume: resumeValue }), config)) as TutorState;
  return {
    message: result.nextTutorMessage,
    state: result,
    ended: result.shouldEnd,
    threadId,
  };
}

/**
 * Read the persisted state for a thread without running the graph.
 * Useful for resuming a session after page refresh.
 */
export async function getTutorState(threadId: string): Promise<TutorState | null> {
  const config = { configurable: { thread_id: threadId } };
  const snap = await graph.getState(config);
  return snap ? (snap.values as TutorState) : null;
}

/**
 * Clear a thread's persisted state by issuing an empty update.
 * (MemorySaver retains the thread but with values reset to defaults.)
 */
export async function clearTutorThread(threadId: string): Promise<void> {
  const config = { configurable: { thread_id: threadId } };
  await graph.updateState(config, {});
}

export function appendTutorTurn(
  log: { role: 'user' | 'assistant'; content: string }[],
  tutorMessage: string
): { role: 'user' | 'assistant'; content: string }[] {
  return [...log, { role: 'assistant', content: tutorMessage }];
}
